import React, { useState, useEffect } from 'react';
import { supabase } from './supabaseClient';

function CoachDashboard({ user }) {
  const [fighters, setFighters] = useState([]);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviting, setInviting] = useState(false);
  const [inviteMsg, setInviteMsg] = useState(null);
  const [selectedFighter, setSelectedFighter] = useState(null);
  const [fighterStats, setFighterStats] = useState(null);
  const [feedbackMsg, setFeedbackMsg] = useState("");
  const [sending, setSending] = useState(false);
  const [coachFeedbacks, setCoachFeedbacks] = useState([]);

  useEffect(() => {
    loadFighters();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadFighters = async () => {
    const { data, error } = await supabase
      .from("coach_fighter")
      .select("*")
      .eq("coach_id", user.id);

    if (error) {
      console.error("Error loading fighters:", error);
      return;
    }

    if (!data || data.length === 0) {
      setFighters([]);
      return;
    }

    const fighterIds = data.map(f => f.fighter_id);
    const { data: profiles } = await supabase
      .from("profiles")
      .select("*")
      .in("id", fighterIds);

    const enriched = data.map(f => ({
      ...f,
      profile: profiles?.find(p => p.id === f.fighter_id) || null
    }));

    setFighters(enriched);
  };

  const handleInvite = async () => {
    if (!inviteEmail) return;
    setInviting(true);
    setInviteMsg(null);

    const { data: profile } = await supabase
      .from("profiles")
      .select("id, email, role")
      .eq("email", inviteEmail)
      .single();

    if (!profile) {
      setInviteMsg({ type: "error", text: "No fighter found with that email." });
      setInviting(false);
      return;
    }

    if (profile.role !== "fighter") {
      setInviteMsg({ type: "error", text: "That user is not registered as a fighter." });
      setInviting(false);
      return;
    }

    const { error } = await supabase
      .from("coach_fighter")
      .insert({ coach_id: user.id, fighter_id: profile.id, status: "active" });

    if (error) {
      setInviteMsg({ type: "error", text: "Fighter already on your team." });
    } else {
      setInviteMsg({ type: "success", text: "Fighter added to your team!" });
      setInviteEmail("");
      loadFighters();
    }
    setInviting(false);
  };

  const loadFighterStats = async (fighter) => {
    setSelectedFighter(fighter);
    setFighterStats(null);
    setCoachFeedbacks([]);

    const { data: stats } = await supabase
      .from("fighter_stats")
      .select("*")
      .eq("user_id", fighter.fighter_id)
      .single();
    setFighterStats(stats);

    const { data: feedbacks } = await supabase
      .from("coach_feedback")
      .select("*")
      .eq("fighter_id", fighter.fighter_id)
      .eq("coach_id", user.id)
      .order("created_at", { ascending: false });
    setCoachFeedbacks(feedbacks || []);
  };

  const sendFeedback = async () => {
    if (!feedbackMsg || !selectedFighter) return;
    setSending(true);
    await supabase.from("coach_feedback").insert({
      coach_id: user.id,
      fighter_id: selectedFighter.fighter_id,
      message: feedbackMsg
    });
    setFeedbackMsg("");
    const { data: feedbacks } = await supabase
      .from("coach_feedback")
      .select("*")
      .eq("fighter_id", selectedFighter.fighter_id)
      .eq("coach_id", user.id)
      .order("created_at", { ascending: false });
    setCoachFeedbacks(feedbacks || []);
    setSending(false);
  };

  return (
    <div style={{padding:"20px"}}>
      <h2 style={{color:"#e94560",marginBottom:"20px"}}>👥 My Fighters</h2>

      <div style={{backgroundColor:"#16213e",padding:"25px",borderRadius:"12px",marginBottom:"25px"}}>
        <h3 style={{color:"white",marginBottom:"15px"}}>Add Fighter to Your Team</h3>
        <div style={{display:"flex",gap:"10px"}}>
          <input
            type="email"
            placeholder="Fighter email address"
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
            style={{flex:1,padding:"12px",backgroundColor:"#0f3460",color:"white",border:"1px solid #0f3460",borderRadius:"8px",fontSize:"1rem"}}
          />
          <button
            onClick={handleInvite}
            disabled={inviting}
            style={{padding:"12px 25px",backgroundColor:"#e94560",color:"white",border:"none",borderRadius:"8px",cursor:"pointer",fontSize:"1rem"}}>
            {inviting ? "Adding..." : "Add Fighter"}
          </button>
        </div>
        {inviteMsg && (
          <p style={{color: inviteMsg.type === "error" ? "#e94560" : "#4caf50", marginTop:"10px"}}>
            {inviteMsg.text}
          </p>
        )}
      </div>

      {fighters.length === 0 ? (
        <div style={{backgroundColor:"#16213e",padding:"40px",borderRadius:"12px",textAlign:"center"}}>
          <p style={{color:"#a8a8b3",fontSize:"1.1rem"}}>No fighters yet. Add your first fighter above!</p>
        </div>
      ) : (
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"20px"}}>
          <div>
            <h3 style={{color:"white",marginBottom:"15px"}}>Your Team ({fighters.length})</h3>
            {fighters.map(f => (
              <div
                key={f.id}
                onClick={() => loadFighterStats(f)}
                style={{backgroundColor: selectedFighter?.id === f.id ? "#0f3460" : "#16213e",padding:"15px 20px",borderRadius:"10px",marginBottom:"10px",cursor:"pointer",border: selectedFighter?.id === f.id ? "2px solid #e94560" : "2px solid #0f3460"}}>
                <p style={{color:"white",margin:0,fontWeight:"bold"}}>🥋 {f.profile?.email || f.fighter_id}</p>
                <p style={{color:"#a8a8b3",margin:"5px 0 0 0",fontSize:"0.85rem"}}>Click to view stats</p>
              </div>
            ))}
          </div>

          {selectedFighter && (
            <div>
              <h3 style={{color:"white",marginBottom:"15px"}}>Fighter Stats</h3>
              {fighterStats ? (
                <div style={{backgroundColor:"#16213e",padding:"20px",borderRadius:"10px",marginBottom:"15px"}}>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"10px",marginBottom:"15px"}}>
                    <div style={{backgroundColor:"#0f3460",padding:"12px",borderRadius:"8px",textAlign:"center"}}>
                      <p style={{color:"#e94560",margin:0,fontSize:"1.3rem",fontWeight:"bold"}}>{fighterStats.xp}</p>
                      <p style={{color:"#a8a8b3",margin:"3px 0 0 0",fontSize:"0.8rem"}}>Total XP</p>
                    </div>
                    <div style={{backgroundColor:"#0f3460",padding:"12px",borderRadius:"8px",textAlign:"center"}}>
                      <p style={{color:"#e94560",margin:0,fontSize:"1.3rem",fontWeight:"bold"}}>{fighterStats.level}</p>
                      <p style={{color:"#a8a8b3",margin:"3px 0 0 0",fontSize:"0.8rem"}}>Level</p>
                    </div>
                    <div style={{backgroundColor:"#0f3460",padding:"12px",borderRadius:"8px",textAlign:"center"}}>
                      <p style={{color:"#e94560",margin:0,fontSize:"1.3rem",fontWeight:"bold"}}>{fighterStats.streak}</p>
                      <p style={{color:"#a8a8b3",margin:"3px 0 0 0",fontSize:"0.8rem"}}>Day Streak</p>
                    </div>
                    <div style={{backgroundColor:"#0f3460",padding:"12px",borderRadius:"8px",textAlign:"center"}}>
                      <p style={{color:"#e94560",margin:0,fontSize:"1.3rem",fontWeight:"bold"}}>{fighterStats.total_uploads}</p>
                      <p style={{color:"#a8a8b3",margin:"3px 0 0 0",fontSize:"0.8rem"}}>Videos</p>
                    </div>
                  </div>
                </div>
              ) : (
                <p style={{color:"#a8a8b3"}}>No stats yet for this fighter.</p>
              )}

              <h3 style={{color:"white",marginBottom:"10px"}}>Send Feedback</h3>
              <textarea
                placeholder="Write coaching feedback for this fighter..."
                value={feedbackMsg}
                onChange={(e) => setFeedbackMsg(e.target.value)}
                rows={4}
                style={{width:"100%",padding:"12px",backgroundColor:"#0f3460",color:"white",border:"1px solid #0f3460",borderRadius:"8px",fontSize:"1rem",marginBottom:"10px",boxSizing:"border-box",resize:"vertical"}}
              />
              <button
                onClick={sendFeedback}
                disabled={sending || !feedbackMsg}
                style={{width:"100%",padding:"12px",backgroundColor: sending || !feedbackMsg ? "#333" : "#e94560",color:"white",border:"none",borderRadius:"8px",cursor: sending || !feedbackMsg ? "not-allowed" : "pointer",fontSize:"1rem",marginBottom:"20px"}}>
                {sending ? "Sending..." : "Send Feedback"}
              </button>

              {coachFeedbacks.length > 0 && (
                <div>
                  <h3 style={{color:"white",marginBottom:"10px"}}>Previous Feedback</h3>
                  {coachFeedbacks.map(fb => (
                    <div key={fb.id} style={{backgroundColor:"#16213e",padding:"15px",borderRadius:"8px",marginBottom:"10px",border:"1px solid #0f3460"}}>
                      <p style={{color:"white",margin:"0 0 5px 0"}}>{fb.message}</p>
                      <p style={{color:"#a8a8b3",margin:0,fontSize:"0.8rem"}}>{new Date(fb.created_at).toLocaleDateString()}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default CoachDashboard;
